import {useInjection} from "inversify-react";
import {useCallback, useEffect, useState} from "react";
import ApiService from "../../services/apiService";
import {
    OrderDetailsDto,
    OrderProductApi, OrderProductCreateDto,
    OrderProductDto, OrderProductUpdateDto, ProductApi,
    ProductDto, ProductImageApi, ProductPriceDto
} from "../../api";
import OrderProductsList from "./orderProductList";
import OrderProductModalFormAddContainer from "./orderProductModalFormAddContainer/orderProductModalFormAddContainer";
import {Layout, message} from "antd";
import {Content} from "antd/lib/layout/layout";
import styles from "./orderProductListContainer.module.scss";
import {ProductPreviewImage} from "./orderProductModalForm/orderProductModalForm";

interface OrderProductListContainerProps {
    order: OrderDetailsDto,
    overStock: boolean,
    setOverStock: (overStock: boolean) => void,
    fetchOrderDetails: () => Promise<any>,
    onFinalizeOrder: () => void;
}

export default function OrderProductsListContainer(props: OrderProductListContainerProps) {
    const apiService = useInjection(ApiService);

    const [isAddFormVisible, setAddFormVisible] = useState(false);
    const [orderProducts, setOrderProducts] = useState<OrderProductDto[] | null>(null);
    const [productImage, setProductImage] = useState<ProductPreviewImage[]>([]);
    const [products, setProducts] = useState<ProductDto[] | null>(null);

    const onDeleteClicked = useCallback(async (id: number) => {
        try {
            await apiService.getApi(OrderProductApi).apiOrderProductIdDelete(id);
            message.success("Pomyślnie usunięto produkt z zamówienia");
            await props.fetchOrderDetails();
        } catch (e: any) {
            message.error("Wystąpił błąd podczas usuwania produktu z zamówienia");
            return e.response?.data;
        } finally {
            await fetchOrderProducts();
            await fetchProducts();
        }
    }, [apiService]);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await apiService.getApi(ProductApi)
                .apiProductGet();
            setProducts(response.data);
            for (const product of response.data) {
                const image = await apiService.getApi(ProductImageApi)
                    .apiProductImageIdFileStreamGet(product.imagesIds![0], {responseType: 'blob'});

                if (!productImage?.find(x => x.productId === product.id)) {
                    setProductImage(productImage => [...productImage, {
                            productId: product.id!,
                            image: URL.createObjectURL(image.data!)
                        }]
                    );
                }
            }
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService]);

    const fetchOrderProducts = useCallback(async () => {
        if (!props.order.id) return;
        try {
            const response = await apiService.getApi(OrderProductApi)
                .apiOrderProductOrderProductsGet(props.order.id);
            setOrderProducts(response.data);
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService, props.order, props.overStock]);

    const onSubmit = useCallback(async (values: OrderProductCreateDto) => {
        try {
            values.orderId = props.order.id;
            await apiService.getApi(OrderProductApi).apiOrderProductPost(values);
            await props.fetchOrderDetails();
            message.success("Pomyślnie dodano produkt do zamówienia");
        } catch (e: any) {
            message.error("Wystąpił błąd podczas dodawania produktu do zamówienia");
            return e.response.data;
        } finally {
            setAddFormVisible(false);
            await fetchOrderProducts();
        }
    }, [apiService, props.order])

    const onOrderProductChange = useCallback(async (product: OrderProductUpdateDto, id: number) => {
        try {
            product.orderId = props.order.id;
            await apiService.getApi(OrderProductApi).apiOrderProductIdPut(id, product);
            message.success("Pomyślnie zmieniono stan zamówienia");
            props.setOverStock(false);
            await fetchOrderProducts();
            await fetchProducts();
            await props.fetchOrderDetails();
        } catch (e: any) {
            message.error("Wystąpił błąd podczas zmiany stanu zamówienia");
            return e.response?.data;
        }
    }, [apiService, props.order]);

    useEffect(() => {
        fetchOrderProducts();
        fetchProducts();
    }, [fetchOrderProducts, fetchProducts]);

    return <Layout style={{backgroundColor: "white"}}>
        <Content>
            <Layout>
                <Content className={styles.content}>
                    <OrderProductsList
                        orderStage={props.order.orderStage!}
                        data={orderProducts}
                        onFinalizeOrder={props.onFinalizeOrder}
                        overStock={props.overStock}
                        productPrice={products?.map(x => x.prices ?? [])
                            .reduce((acc: ProductPriceDto[], curr: ProductPriceDto[]) => acc.concat(curr), []) ?? []}
                        onAddClicked={() => setAddFormVisible(true)}
                        onDeleteClicked={onDeleteClicked}
                        onAmountChange={onOrderProductChange}
                        paymentMethod={props.order.paymentMethod!}
                        deliveryMethod={props.order.deliveryMethod!}
                    />
                    <OrderProductModalFormAddContainer
                        onFinish={() => setAddFormVisible(false)}
                        onSubmit={onSubmit}
                        addFormVisibility={isAddFormVisible}
                        title={"Dodaj nowy produkt do zamówienia"}
                        productImage={productImage}
                        products={products ?? []}
                    />
                </Content>
            </Layout>
        </Content>
    </Layout>;
}
